import { isEthereumLikeToken, isEthereumLike } from '@exodus/ethereum-lib';
import { Order } from '@exodus/models';
import { VersionedTransaction } from '@exodus/solana-web3.js';
import createFetchival from '@exodus/fetch/create-fetchival';
import ExodusModule from '@exodus/module';
import { SwapMode } from '@exodus/exchange-client-providers';
import { ExchangeStatus } from './constants';
const TX_TYPES = {
    PRE_SETUP: 'preSetupTransaction',
    SETUP: 'setupTransaction',
    SWAP: 'swapTransaction',
    CLEANUP: 'cleanupTransaction',
    DEPOSIT: 'depositTransaction',
    PAYOUT: 'payoutTransaction',
};
const MODULE_ID = 'exchange';
const TAG_NAME_BY_ASSET_NAME = new Map([
    ['ripple', 'destTag'],
    ['nem', 'message'],
    ['stellar', 'memo'],
    ['cosmos', 'memo'],
    ['eosio', 'memo'],
    ['terra', 'memo'],
    ['hedera', 'memo'],
    ['algorand', 'memo'],
    ['injective', 'memo'],
    ['kava', 'memo'],
    ['osmosis', 'memo'],
]);
export class Exchange extends ExodusModule {
    #analytics;
    #activeWalletAccountAtom;
    #addressProvider;
    #analyticsUserIdAtom;
    #config;
    #exchangeApproveTokens;
    #exchangeAssets;
    #exchangeGetAllowance;
    #exchangeProviders;
    #exchangeQuotesAtom;
    #exchangeRemoteConfigAtom;
    #exchangeStatusAtom;
    #fetchival;
    #getBuildMetadata;
    #logger;
    #orders;
    #transactionSigner;
    #txSend;
    #walletAccounts;
    #referralsAtom;
    #tracer;
    constructor(params) {
        super({ MODULE_ID, logger: params.logger });
        this.#fetchival = createFetchival({ fetch: params.fetch });
        this.#activeWalletAccountAtom = params.activeWalletAccountAtom;
        this.#addressProvider = params.addressProvider;
        this.#analyticsUserIdAtom = params.analyticsUserIdAtom;
        this.#config = params.config;
        this.#exchangeApproveTokens = params.exchangeApproveTokens;
        this.#exchangeAssets = params.exchangeAssets;
        this.#exchangeGetAllowance = params.exchangeGetAllowance;
        this.#exchangeProviders = params.exchangeProviders;
        this.#exchangeQuotesAtom = params.exchangeQuotesAtom;
        this.#exchangeRemoteConfigAtom = params.exchangeRemoteConfigAtom;
        this.#exchangeStatusAtom = params.exchangeStatusAtom;
        this.#getBuildMetadata = params.getBuildMetadata;
        this.#logger = params.logger;
        this.#orders = params.orders;
        this.#transactionSigner = params.transactionSigner;
        this.#txSend = params.txSend;
        this.#walletAccounts = params.walletAccounts;
        this.#referralsAtom = params.referralsAtom;
        this.#tracer = params.exchangeTracer;
        this.#analytics = params.analytics;
    }
    prepareExchange = async ({ fromAssetName, exchangeButton, swapMode = SwapMode.ExactIn, toAssetName, fromAmount, toAmount, fromWalletAccount: _fromWalletAccount, toWalletAccount: _toWalletAccount, providerName, rateId, slippage, isSendAll, quoteId, }) => {
        this.#tracer?.clear();
        const traceMessage = this._traceMessageScope('prepareExchange');
        this.#tracer?.traceTime('TotalExchange');
        this.#tracer?.traceTime('PrepareExchange');
        let createdOrderId;
        try {
            const quotes = await this.#exchangeQuotesAtom.get();
            const quote = quotes[quoteId]?.quote;
            if (!quote) {
                throw new Error(`Quote not found for quoteId: ${quoteId}`);
            }
            if (quote.rateId !== rateId) {
                traceMessage(`RateId mismatch: ${quote.rateId} !== ${rateId}`);
            }
            await this.#exchangeStatusAtom.set({
                status: ExchangeStatus.STARTED,
                fromAssetName,
                toAssetName,
                fromAmount,
                toAmount,
                error: null,
                orderId: null,
            });
            const fromAsset = this.#exchangeAssets.getAsset({ assetName: fromAssetName });
            const toAsset = this.#exchangeAssets.getAsset({ assetName: toAssetName });
            const activeWalletAccount = await this.#activeWalletAccountAtom.get();
            const fromWalletAccount = _fromWalletAccount || activeWalletAccount;
            const toWalletAccount = _toWalletAccount || activeWalletAccount;
            const fromAddress = await this.#addressProvider.getReceiveAddress({
                assetName: fromAsset.name,
                walletAccount: this.#walletAccounts.get(fromWalletAccount),
            });
            let toAddress = await this.#addressProvider.getReceiveAddress({
                assetName: toAsset.name,
                walletAccount: this.#walletAccounts.get(toWalletAccount),
            });
            const remoteConfig = await this.#exchangeRemoteConfigAtom.get();
            const anonymousId = await this.#analyticsUserIdAtom?.get();
            const provider = this.#getProvider(providerName);
            const referrals = await this.#referralsAtom?.get();
            const referralCode = referrals?.referralCode;
            let fromAddressTag, toAddressTag;
            if (toAsset.api.features?.abstractAccounts) {
                traceMessage('Getting Account Name');
                const { accountCreator, accountCreatorMemo, mainAccountName } = await toAsset.api
                    .getAbstractAccountName({
                    walletAccount: toWalletAccount,
                });
                if (accountCreator) {
                    toAddress = accountCreator;
                    toAddressTag = accountCreatorMemo;
                }
                else {
                    toAddress = mainAccountName;
                }
            }
            traceMessage('preparing exchange');
            const { transaction, providerOrderId, approvalAddress, fromAmount: swappedAmount, } = await provider.prepareExchange({
                fromAmount,
                fromAddress: fromAddress.toString(),
                fromAddressTag,
                toAmount,
                pairId: `${fromAsset.ticker}_${toAsset.ticker}`,
                toAddress: toAddress.toString(),
                toAddressTag,
                slippage,
                isSendAll,
                anonymousId,
                referralCode,
                swapMode,
                includePartners: remoteConfig[providerName]?.includePartners,
                excludePartners: remoteConfig[providerName]?.excludePartners,
                quote,
                onOrderIdGenerated: (id) => (createdOrderId = id),
                simulateTx: ({ baseAssetName, ...args }) => {
                    traceMessage('simulating tx');
                    const asset = this.#exchangeAssets.getAsset({ assetName: baseAssetName });
                    return asset.api.web3.simulateTransactions(args);
                },
                signRawTx: (args) => this.#signTx({ walletAccount: fromWalletAccount, ...args }),
                createTx: (args) => this.#createTx({ walletAccount: fromWalletAccount, ...args }),
                getAllowance: (args) => this.#exchangeGetAllowance.getAllowance({ walletAccount: fromWalletAccount, ...args }),
            });
            await this.#exchangeStatusAtom.set((prev) => {
                if (prev)
                    return { ...prev, orderId: createdOrderId };
                return prev;
            });
            return {
                transaction,
                providerOrderId,
                orderId: createdOrderId,
                approvalAddress,
                fromAmount: swappedAmount,
            };
        }
        catch (err) {
            if (!createdOrderId && err.orderId) {
                createdOrderId = err.orderId;
            }
            if (createdOrderId) {
                this.#logExchangeError({
                    orderId: createdOrderId,
                    error: err,
                });
            }
            this.#trackExchangeTimes({ providerName, orderId: createdOrderId });
            await this.#exchangeStatusAtom.set({
                status: ExchangeStatus.FAILED,
                fromAssetName,
                toAssetName,
                fromAmount,
                toAmount,
                orderId: createdOrderId || null,
                error: err.stack || err.message || err.toString(),
            });
            throw err;
        }
        finally {
            if (createdOrderId) {
                const _exchangeButton = exchangeButton === 'min' ? 'm' : exchangeButton === 'half' ? 'h' : exchangeButton;
                this.#logExchangeMetadata({
                    orderId: createdOrderId,
                    buttonPressed: _exchangeButton,
                    exchangeDirection: swapMode === 'ExactIn' ? 'from' : 'to',
                    rateId,
                }).catch((err) => this.#logger.error(`Failed to log metadata: ${err.message || err.toString()}`));
            }
            this.#tracer?.traceTimeEnd('PrepareExchange');
        }
    };
    executeExchange = async ({ orderId, transaction, providerName, fromAmount, fromAssetName, toAssetName, toAmount, fromWalletAccount: _fromWalletAccount, toWalletAccount: _toWalletAccount, approvalAddress, providerOrderId: _providerOrderId, }) => {
        try {
            this.#tracer?.traceTime('ExecuteExchange');
            const traceMessage = this._traceMessageScope('executeExchange');
            const provider = this.#getProvider(providerName);
            const fromAsset = this.#exchangeAssets.getAsset({ assetName: fromAssetName });
            const toAsset = this.#exchangeAssets.getAsset({ assetName: toAssetName });
            const activeWalletAccount = await this.#activeWalletAccountAtom.get();
            const fromWalletAccount = _fromWalletAccount || activeWalletAccount;
            traceMessage('executing exchange');
            const { transactions, providerOrderId, svc } = await provider.executeExchange({
                approvalAddress,
                pairId: `${fromAsset.ticker}_${toAsset.ticker}`,
                orderId,
                transaction,
                fromAmount,
                broadcastRawTx: this.#broadcastRawTx,
                sendTx: this.#sendTx,
                approveTokens: (args) => this.#exchangeApproveTokens.approveTokens({ walletAccount: fromWalletAccount, ...args }),
                getAllowance: (args) => this.#exchangeGetAllowance.getAllowance({ walletAccount: fromWalletAccount, ...args }),
                providerOrderId: _providerOrderId,
            });
            try {
                const activeWalletAccount = await this.#activeWalletAccountAtom.get();
                const fromWalletAccount = _fromWalletAccount || activeWalletAccount;
                const toWalletAccount = _toWalletAccount || activeWalletAccount;
                if (transactions.length === 0) {
                    throw new Error('No transactions returned from exchange provider');
                }
                const fromTx = transactions.find((tx) => [TX_TYPES.SWAP, TX_TYPES.DEPOSIT].includes(tx.type));
                const fromTxId = fromTx?.id || transactions[0]?.id;
                if (!fromTxId) {
                    throw new Error(`Cannot determine tx id from transactions: ${transactions}`);
                }
                const orderData = {
                    orderId: providerOrderId,
                    txIds: transactions.map((tx) => ({ type: tx.type, txId: tx.id })),
                    fromAmount,
                    fromAsset: fromAssetName,
                    toAmount,
                    toAsset: toAssetName,
                    fromTxId,
                    toTxId: null,
                    fromWalletAccount,
                    toWalletAccount,
                    svc,
                };
                traceMessage('upserting order');
                this.#orders.upsert([Order.fromJSON(orderData)]).catch((err) => {
                    traceMessage(`Failed to upsert order: ${err.message}\nOrder: ${JSON.stringify(orderData)}`);
                    throw err;
                });
                return orderData.orderId;
            }
            catch (err) {
                this.#logExchangeError({ orderId, error: err });
            }
            finally {
                await this.#exchangeStatusAtom.set({
                    status: ExchangeStatus.IN_PROGRESS,
                    fromAssetName,
                    toAssetName,
                    fromAmount,
                    toAmount,
                    orderId: providerOrderId,
                    error: null,
                });
            }
        }
        catch (err) {
            this.#logExchangeError({
                orderId,
                error: err,
            });
            await this.#exchangeStatusAtom.set({
                status: ExchangeStatus.FAILED,
                fromAssetName,
                toAssetName,
                fromAmount,
                toAmount,
                orderId: orderId || null,
                error: err.stack || err.message || err.toString(),
            });
            throw err;
        }
        finally {
            this.#tracer?.traceTimeEnd('ExecuteExchange');
            this.#tracer?.traceTimeEnd('TotalExchange');
            this.#trackExchangeTimes({ providerName, orderId });
        }
    };
    exchange = async (args) => {
        const resp = await this.prepareExchange(args);
        return this.executeExchange({
            ...args,
            ...resp,
            orderId: resp.orderId,
        });
    };
    ackExchange = () => {
        this.#tracer?.clear();
        this.#exchangeStatusAtom.set(null);
    };
    cancelExchange = (orderId, message) => {
        const traceMessage = this._traceMessageScope('cancelExchange');
        traceMessage('cancelling exchange');
        this.#exchangeStatusAtom.set(null);
        const error = new Error(message ?? 'exchange cancelled by the user');
        this.#logExchangeError({ orderId, error });
    };
    #getProvider = (providerName) => {
        const provider = this.#exchangeProviders.getProvider(providerName);
        if (!provider) {
            throw new Error(`Unknown provider: ${providerName}`);
        }
        return provider;
    };
    #logExchangeError = async ({ orderId, error, }) => {
        const title = error.message || error.toString();
        this.#tracer?.traceError(error);
        const trace = this.#tracer?.print();
        return this.#request(`orders/${encodeURIComponent(orderId)}/logs`).post({
            type: 'error',
            title,
            ...(trace ? { description: trace.slice(0, 3000) } : {}),
        });
    };
    #trackExchangeTimes = async (params) => {
        const times = this.#tracer?.timeLogs;
        if (times && this.#analytics) {
            this.#analytics.track({
                event: 'ExchangeTimeTrace',
                properties: {
                    ...Object.fromEntries(times.entries()),
                    ProviderName: params.providerName,
                    OrderId: params.orderId || 'N/A',
                },
            });
        }
    };
    #logExchangeMetadata = async ({ orderId, buttonPressed, exchangeDirection, rateId, }) => {
        const buildMetadata = await this.#getBuildMetadata();
        const { build: clientBuild, dev: isDev, osName: clientOs, platformName: clientOsVersion, } = buildMetadata;
        return this.#request(`orders/${encodeURIComponent(orderId)}/metadata`).post({
            buttonPressed,
            clientBuild,
            clientOs,
            clientOsVersion,
            exchangeDirection,
            isDev,
            rateId,
        });
    };
    #request = (path) => {
        const { appPlatform } = this.#config.providers.exodus;
        return this.#fetchival(new URL(`${this.#config.providers.exodus.baseUrl}/${path}`), {
            headers: {
                'App-Name': this.#config.providers.exodus.appName,
                'App-Version': this.#config.providers.exodus.appVersion,
                ...(appPlatform ? { 'App-Platform': appPlatform } : {}),
            },
        });
    };
    #signTx = async ({ assetId, rawTx: unsignedRawTx, walletAccount, }) => {
        const asset = this.#exchangeAssets.getAsset({ assetId });
        const serializedTxBuffer = Buffer.from(unsignedRawTx, 'base64');
        const walletAccountInstance = this.#walletAccounts.get(walletAccount);
        const unsignedTx = {
            txData: {
                transaction: VersionedTransaction.deserialize(serializedTxBuffer),
                transactionBuffer: serializedTxBuffer,
            },
            txMeta: {
                assetName: asset.baseAsset.name,
                accountIndex: walletAccountInstance.index,
            },
        };
        const { rawTx } = await this.#transactionSigner.signTransaction({
            baseAssetName: asset.baseAsset.name,
            unsignedTx,
            walletAccount: walletAccountInstance,
        });
        return typeof rawTx === 'string' ? rawTx : rawTx.toString('base64');
    };
    #createCexTx = async ({ asset, recipient, amount, walletAccount, opts, isSendAll, feeOpts, }) => {
        let txInput;
        const isBaseAsset = asset.baseAsset.name === asset.name;
        if (opts?.pidBytes) {
            const _txInput = isBaseAsset
                ? []
                : isEthereumLikeToken(asset)
                    ? asset.contract.transfer.build(recipient.toLowerCase(), amount.toBaseString({ unit: false }))
                    : [];
            txInput = Buffer.from(new Uint8Array([..._txInput, ...opts.pidBytes]));
        }
        const feeAmount = 'fee' in feeOpts ? feeOpts.fee : asset.feeAsset.currency.ZERO;
        const to = isBaseAsset
            ? recipient
            : (isEthereumLikeToken(asset) && asset.contract.address) || recipient;
        let options = {
            to,
        };
        if (opts?.payInAddressTag) {
            options = {
                ...options,
                ...this.#getTxOptions(asset, opts.payInAddressTag),
            };
        }
        return {
            ...(txInput ? { txInput, keepTxInput: true } : {}),
            assetName: asset.name,
            receiver: {
                address: recipient,
                amount,
            },
            silent: false,
            walletAccount,
            isSendAll: Boolean(isSendAll),
            isRbfAllowed: false,
            isExchange: true,
            feeOpts: { ...feeOpts, isSendAll },
            feeAmount,
            options,
        };
    };
    #createEVMTx = ({ asset, gasLimit, gasPrice, tipGasPrice, to, amount, walletAccount, txInput, isSendAll, }) => {
        return {
            assetName: asset.baseAsset.name,
            chainId: asset.baseAsset.chainId,
            keepTxInput: true,
            gasLimit,
            gasPrice,
            tipGasPrice,
            receiver: {
                address: to,
                amount: asset.baseAsset.name === asset.name
                    ? asset.currency.baseUnit(amount)
                    : asset.baseAsset.currency.ZERO,
            },
            silent: false,
            txInput,
            walletAccount,
            isSendAll: Boolean(isSendAll),
            isExchange: true,
        };
    };
    #createTx = async ({ amount, assetId, recipient, txData, opts, isSendAll, walletAccount, feeOpts, }) => {
        try {
            const asset = this.#exchangeAssets.getAsset({ assetId });
            return txData && isEthereumLike(asset)
                ? this.#createEVMTx({
                    asset,
                    gasLimit: txData.gasLimit,
                    gasPrice: txData.gasPrice,
                    tipGasPrice: txData.tipGasPrice,
                    to: txData.to,
                    amount: txData.value,
                    walletAccount,
                    txInput: txData.data,
                    isSendAll,
                })
                : await this.#createCexTx({
                    asset,
                    recipient: recipient,
                    amount: amount,
                    walletAccount,
                    opts,
                    isSendAll,
                    feeOpts: feeOpts,
                });
        }
        catch (err) {
            this.#logger.error('Failed to create tx', err);
            throw err;
        }
    };
    #sendTx = async (tx) => {
        try {
            return await this.#txSend(tx);
        }
        catch (err) {
            this.#logger.error('Failed to send tx', err);
            throw err;
        }
    };
    #getTxOptions = (asset, payInAddressTag) => {
        const tagName = TAG_NAME_BY_ASSET_NAME.get(asset.baseAsset.name);
        if (tagName) {
            return { [tagName]: payInAddressTag };
        }
        this.#logger.warn(`Unknown tx options for ${asset.name}: ${payInAddressTag}`);
        return undefined;
    };
    #broadcastRawTx = async ({ assetId, rawTx }) => {
        const asset = this.#exchangeAssets.getAsset({ assetId });
        const txId = await asset.baseAsset.api.broadcastTx(rawTx);
        return { txId };
    };
    _traceMessageScope(scope) {
        return (message) => this.#tracer?.traceMessage(`exchange:${scope}:${message}`);
    }
}
const exchangeFactory = (args) => new Exchange({ ...args });
const exchangeModuleDefinition = {
    id: MODULE_ID,
    type: 'module',
    factory: exchangeFactory,
    dependencies: [
        'analytics?',
        'activeWalletAccountAtom',
        'addressProvider',
        'analyticsUserIdAtom?',
        'config',
        'exchangeApproveTokens',
        'exchangeAssets',
        'exchangeGetAllowance',
        'exchangeProviders',
        'exchangeQuotesAtom',
        'exchangeRemoteConfigAtom',
        'exchangeStatusAtom',
        'fetch',
        'getBuildMetadata',
        'logger',
        'orders',
        'referralsAtom?',
        'transactionSigner',
        'txSend',
        'walletAccounts',
        'exchangeTracer?',
    ],
};
export default exchangeModuleDefinition;
